import React, { useContext, useState } from "react";

import { Modal } from "../../components/ui";
import { UserContext } from "../../contexts";
import { getFileUrl } from "../../core/config/api.js";
import { useStoreWriteGuard } from "../../helpers/storeScope";
import OrderStatus from "../../components/order/Status";
import Quotes from "./Quotes";
import { ItemStateProvider } from "../hooks";
import AdminActions from "./SourcedItem/AdminActions";
import ClientActions from "./SourcedItem/ClientActions";
import "../../views/Orders/rc-order-drawer.scss";

/**
 * An order's quotes, as the page's right-hand panel.
 *
 * Guillaume, 2026-09-01: "one side of the page is the lines and on the right side a panel that
 * opens — the best way is for the right panel to be the quotation."
 *
 * This is the same arrangement as the User CRM: the list holds its own column, the panel opens
 * beside it rather than over it, and one shared ratio keeps the two meeting on the same line.
 *
 * It replaces the inline disclosure that used to push quotes into the row itself. A row can now
 * never grow — a deal with nine quotes reads exactly like a deal with one, and the list stays
 * scannable. The row's actions moved in here too: the panel IS the order, so its commands belong
 * with it instead of competing with the facts on the line.
 *
 * Two things the panel owns that the inline version did not have to:
 *
 *  - `ItemStateProvider`. It used to come from the ROW component, and the panel mounts at page
 *    level, outside it. constate's consumer then returns its no-provider sentinel — an empty
 *    object, NOT an error — so `isSelectingQuotes` read `undefined` (the list stayed collapsed)
 *    and `setSelectedQuote` was not a function (saving a quote threw). One `console.warn` was the
 *    only symptom.
 *  - the open quote. Opening one used to PUSH `/requests/orders/:id/:quoteId`, which matches a
 *    different <Route> element, remounting `Requests` and discarding `selectedOrder` — so the act
 *    of opening a quote closed the panel it was opened from. Here it is local state.
 */
const OrderQuotesDrawer = ({ item, open, onClose, onRefresh }) => {
  const { user } = useContext(UserContext);
  // A quote belongs to a store; from "Master Store" there is no single one to save it against.
  const { blocked: storeGuardBlocked, reason: storeGuardReason } = useStoreWriteGuard();
  const [openQuoteId, setOpenQuoteId] = useState(null);
  if (!item) return null;

  const brand = item?.product?.brand?.name;
  const model = item?.product?.model?.name;

  return (
    <Modal
      open={open}
      onClose={onClose}
      variant="drawer"
      size="lg"
      // The panel opens BESIDE the list on laptop, so the page behind it is still the thing being
      // read from — freezing its scroll (the default for a modal that covers the viewport) left
      // the orders list stuck for as long as the panel was open.
      lockScroll={false}
      // A titled bar, not a bare × floating in an empty header row. Without the title the only
      // way out of the panel was a muted glyph with nothing beside it, which read as blank space
      // — "there's no close button to close it" (Alex, 2026-09-16). The word also tells you what
      // the panel is, so the quotes below it are visibly this order's rather than a second list.
      // It replaces the section heading that used to sit inside the scrolling area.
      title="Quotes"
      // A bare X, no word beside it. The label was added because the button was
      // reported missing twice — but the real cause was that the panel ran a
      // full viewport wide from the list edge, putting the button ~900px off
      // the screen. That is fixed in the stylesheet, so the glyph can go back
      // to being a glyph.
      className="orders-dark rc-order-drawer"
    >
      <ItemStateProvider item={item}>
        <div className="rc-order-drawer__body">
          <header className="rc-order-drawer__hero">
            <span className="rc-order-drawer__photo">
              <img src={getFileUrl(item?.product?.images?.[0]?.id)} alt="" />
            </span>

            <div className="rc-order-drawer__id">
              <div className="rc-order-drawer__nameline">
                <h2 className="rc-order-drawer__brand">{brand}</h2>
                <OrderStatus status={item?.status} />
              </div>
              {model ? <p className="rc-order-drawer__model">{model}</p> : null}
              {item.product?.reference_number ? (
                <p className="rc-order-drawer__ref">{item.product.reference_number}</p>
              ) : null}
            </div>

            <div className="rc-order-drawer__actions">
              {user?.is_admin ? (
                <>
                  <AdminActions item={item} refreshParent={onRefresh} hide={onClose} />
                  {/* Sat in the quote list's toolbar before, where it read as a filter-row
                      control. Writing a quote is the panel's primary command — it belongs with
                      VIEW, not below two dropdowns. */}
                  <button
                    type="button"
                    className="rc-order-drawer__new-quote"
                    disabled={storeGuardBlocked}
                    title={storeGuardReason}
                    onClick={() => setOpenQuoteId("new")}
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
                      <path d="M7 1v12M1 7h12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    </svg>
                    New Quote
                  </button>
                </>
              ) : (
                <ClientActions item={item} />
              )}
            </div>
          </header>

          <section className="rc-order-drawer__quotes">
            <Quotes
              item={item}
              refreshParent={onRefresh}
              panel
              quoteId={openQuoteId}
              onSelectQuote={setOpenQuoteId}
            />
          </section>
        </div>
      </ItemStateProvider>
    </Modal>
  );
};

export default OrderQuotesDrawer;
